// ─── Public Portal settings ─────────────────────────────────────────────────
//
// Renders a summary card + configuration modal for the per-project
// public portal. Users toggle the portal on/off, pick which
// dashboards visitors can see, and choose which gallery albums are
// public.
//
// Config is persisted to localStorage keyed by (projectId, userId)
// until the backend endpoint lands (see the TODO next to `persist`).
// When the API ships, swap the two localStorage helpers for
// axios.get/put + the panel keeps its shape.
import { useEffect, useState } from 'react'
import { X, Loader } from 'lucide-react'
import axios from 'axios'
import { dashboardApi } from '../../../lib/dashboardApi'

const API_URL       = import.meta.env.VITE_API_URL       || ''
const API_BASE_PATH = import.meta.env.VITE_API_BASE_PATH || '/api'
const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
// Same storage lookup projectsApi/messageApi use — Remember-Me stores
// the JWT in localStorage, session-only logins keep it in
// sessionStorage. Reading `auth-store` was wrong (that's Zustand's
// state, not the raw token) and made every Public Portal fetch
// return 401 → axios auth-interceptor logged the user out.
function _getToken() {
  return localStorage.getItem(JWT_STORAGE_KEY) || sessionStorage.getItem(JWT_STORAGE_KEY) || ''
}

// Fetch the project's gallery albums from the photo API. Returns
// `[{id, name}, …]` — id is the numeric Album PK stringified so it
// round-trips through the JSONField on the backend without type
// mismatch on save/load.
async function fetchProjectAlbums(projectId) {
  const token = _getToken()
  // Photo Album viewset supports `?project=<id>` filtering. Ask for a
  // big page — projects rarely have more than a few dozen albums, but
  // set it high enough that we don't paginate silently.
  const res = await axios.get(
    `${API_URL}${API_BASE_PATH}/photo/album/?project=${projectId}&page_size=500`,
    { headers: { Authorization: token ? `Bearer ${token}` : '' } },
  )
  const raw = res.data?.results || res.data?.data || res.data || []
  const rows = Array.isArray(raw) ? raw : []
  return rows.map((a) => ({
    id:   String(a.id),
    name: a.name || `Album #${a.id}`,
  }))
}

const DEFAULT_CONFIG = {
  enabled:                true,
  dashboard_ids:          [],          // ids from /templates/available_for_project/
  default_dashboard_id:   null,        // id of the picked default (must be in dashboard_ids)
  // Album ids are numeric primary keys, stringified for JSON stability.
  album_ids:              [],
}

// Server-backed load / save. Fall back to defaults on any error so
// the modal always renders — the operator can retry Save if the PUT
// fails and we show the error inline.
async function loadConfigApi(projectId) {
  const token = _getToken()
  const res = await axios.get(
    `${API_URL}${API_BASE_PATH}/project/${projectId}/public-portal/`,
    { headers: { Authorization: token ? `Bearer ${token}` : '' } },
  )
  return { ...DEFAULT_CONFIG, ...(res.data || {}) }
}
async function saveConfigApi(projectId, cfg) {
  const token = _getToken()
  await axios.put(
    `${API_URL}${API_BASE_PATH}/project/${projectId}/public-portal/`,
    cfg,
    { headers: { Authorization: token ? `Bearer ${token}` : '', 'Content-Type': 'application/json' } },
  )
}

export default function PublicPortalPanel({ projectId, onClose, readOnly = false }) {
  const [config, setConfig] = useState({ ...DEFAULT_CONFIG })
  const [loading, setLoading] = useState(true)
  const [saveError, setSaveError] = useState(null)

  // Live gallery-album list for this project. Fetched in parallel
  // with the portal config so the modal only pops up once both
  // resolve, avoiding a jarring second load.
  const [albums,        setAlbums]        = useState([])
  const [albumsError,   setAlbumsError]   = useState(null)

  useEffect(() => {
    if (!projectId) return
    let cancelled = false
    setLoading(true)
    setAlbumsError(null)
    Promise.all([
      loadConfigApi(projectId).catch(() => ({ ...DEFAULT_CONFIG })),
      fetchProjectAlbums(projectId).catch((e) => {
        if (!cancelled) setAlbumsError(e?.message || 'Failed to load albums')
        return []
      }),
    ]).then(([cfg, rows]) => {
      if (cancelled) return
      // Coerce any legacy string ids (from the placeholder list this
      // component used to ship with) into the current shape so the
      // checkboxes stay in sync with what the user last saved.
      setConfig({
        ...DEFAULT_CONFIG,
        ...cfg,
        album_ids: (cfg.album_ids || []).map((v) => String(v)),
      })
      setAlbums(rows)
    }).finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [projectId])

  if (loading) {
    return (
      <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-2xl px-6 py-4 flex items-center gap-2 text-sm text-gray-700">
          <Loader className="w-4 h-4 animate-spin" /> Loading Public Portal…
        </div>
      </div>
    )
  }

  return (
    <PublicPortalConfigModal
      projectId={projectId}
      initial={config}
      albums={albums}
      albumsError={albumsError}
      saveError={saveError}
      readOnly={readOnly}
      onCancel={() => { if (onClose) onClose() }}
      onSave={async (next) => {
        setSaveError(null)
        try {
          await saveConfigApi(projectId, next)
          setConfig(next)
          if (onClose) onClose()
        } catch (e) {
          setSaveError(e?.response?.data?.error || e?.message || 'Save failed')
        }
      }}
    />
  )
}

// ─── Configuration modal ──────────────────────────────────────────────────
function PublicPortalConfigModal({ projectId, initial, albums, albumsError, saveError, onCancel, onSave, readOnly = false }) {
  const [enabled,      setEnabled]      = useState(!!initial.enabled)
  const [selectedIds,  setSelectedIds]  = useState(initial.dashboard_ids || [])
  const [defaultId,    setDefaultId]    = useState(initial.default_dashboard_id || null)
  const [albumIds,     setAlbumIds]     = useState(initial.album_ids || [])

  // Load active dashboards for this project. Any pre-existing config
  // that references a dashboard no longer on the project silently
  // drops out when the user next hits Save.
  const [dashboards, setDashboards] = useState([])
  const [dashboardsLoading, setDashboardsLoading] = useState(false)
  const [dashboardsError, setDashboardsError]     = useState(null)
  useEffect(() => {
    if (!projectId) { setDashboards([]); return }
    let cancelled = false
    setDashboardsLoading(true); setDashboardsError(null)
    dashboardApi.getProjectTemplates(projectId)
      .then((res) => {
        if (cancelled) return
        const rows = res?.data?.templates || res?.data?.results || res?.data || []
        const active = (Array.isArray(rows) ? rows : []).filter((t) => t.is_active !== false)
        setDashboards(active)
      })
      .catch((e) => { if (!cancelled) setDashboardsError(e?.message || 'Failed to load dashboards') })
      .finally(() => { if (!cancelled) setDashboardsLoading(false) })
    return () => { cancelled = true }
  }, [projectId])

  // First-run seed — check every active dashboard by default so a fresh
  // Public Portal setup mirrors what the project owner already sees.
  useEffect(() => {
    if (!dashboards.length) return
    if (selectedIds.length === 0) {
      const ids = dashboards.map((d) => d.id)
      setSelectedIds(ids)
      setDefaultId(ids[0])
    } else if (!selectedIds.some((id) => dashboards.some((d) => d.id === id))) {
      // Stored ids don't match any current dashboards — clear.
      setSelectedIds([])
      setDefaultId(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboards])

  // Keep default dashboard in sync with the selected list.
  useEffect(() => {
    if (!selectedIds.includes(defaultId)) {
      setDefaultId(selectedIds[0] || null)
    }
  }, [selectedIds, defaultId])

  const toggleDash  = (id) => setSelectedIds((prev) =>
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
  )
  const toggleAlbum = (id) => setAlbumIds((prev) =>
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
  )

  const publicUrl  = `${(typeof window !== 'undefined' && window.location?.origin) || 'https://www.wqdatalive.com'}/public/${projectId || ''}`
  const galleryUrl = `${publicUrl}#gallery`

  const submit = () => onSave({
    enabled,
    dashboard_ids:        selectedIds,
    default_dashboard_id: defaultId,
    album_ids:            albumIds,
  })

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-start justify-center p-4 pt-10"
         onClick={onCancel}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-8 py-6 border-b border-gray-100 flex items-center justify-between">
          <h2 className="common-head25 text-lg font-bold text-[#4D4D4D]">Public Portal</h2>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600" title="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-8 py-6 overflow-y-auto">
          {/* Read-only outside Edit Mode: banner inside the modal + a disabled
              fieldset so every control below is non-interactive (values stay
              readable). Rendering the notice here — not by wrapping the whole
              fixed-overlay modal in EditModeGate — keeps the modal layout intact. */}
          {readOnly && (
            <div className="mb-5 px-4 py-2.5 bg-[#F1F2F4] border border-gray-200 rounded text-sm text-[#4D4D4D]">
              Public Portal settings are read-only. Choose <strong>Settings &rarr; Edit Mode</strong> to make changes.
            </div>
          )}
          <fieldset disabled={readOnly} className="border-0 p-0 m-0 min-w-0 space-y-5">
          {/* ── Public Portal Link ── */}
          <section>
            <h3 className="common-size18 text-sm font-bold text-[#4D4D4D] mb-3">Public Portal Link</h3>
            <p className="common-size15 font-semibold text-xs text-[#4D4D4D] mb-1">Your project&apos;s public portal link:</p>
            <a href={publicUrl} target="_blank" rel="noreferrer" className="common-size15 font-semibold text-[#0866AB] hover:underline text-sm break-all">
              {publicUrl}
            </a>
            <label className="common-size15 mt-2 flex items-center gap-2 text-sm text-[#4D4D4D]">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="rounded accent-clr"
              />
              Enabled
            </label>
          </section>

          <div className="border-t border-gray-100" />

          {/* ── Public Dashboard Selection ── */}
          <section>
            <h3 className="common-size18 text-sm font-bold text-[#4D4D4D] mb-4">Public Dashboard Selection</h3>
            <p className="common-size15 text-xs text-[#4D4D4D] mb-4">
              <strong>Note:</strong> Public Dashboards will only display those sites and parameter data that are enabled for display in the settings section above.
            </p>
            <p className="common-size15 text-xs text-[#4D4D4D] font-semibold mb-4">Please select dashboards that you want to display in public portal:</p>
            {dashboardsLoading && (
              <div className="common-size15 flex items-center gap-2 text-xs text-gray-500 py-2">
                <Loader className="w-3.5 h-3.5 animate-spin" /> Loading dashboards…
              </div>
            )}
            {dashboardsError && !dashboardsLoading && (
              <div className="common-size15 text-xs text-red-600 py-1">{dashboardsError}</div>
            )}
            {!dashboardsLoading && !dashboardsError && dashboards.length === 0 && (
              <p className="common-size15 text-xs text-gray-500 italic py-2">
                No active dashboards on this project yet. Add one from the Dashboard menu.
              </p>
            )}
            <div className="space-y-4">
              {dashboards.map((d) => (
                <label key={d.id} className="common-size18 flex items-center gap-2 font-semibold text-sm text-[#4D4D4D]">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(d.id)}
                    onChange={() => toggleDash(d.id)}
                    className="rounded accent-clr"
                  />
                  {d.name}
                </label>
              ))}
            </div>
            <div className="mt-5 flex items-center gap-3">
              <label className="common-size15 text-sm font-semibold text-[#4D4D4D]">Default Dashboard</label>
              <select
                value={defaultId || ''}
                onChange={(e) => setDefaultId(e.target.value ? Number(e.target.value) : null)}
                disabled={selectedIds.length === 0}
                className="brdr-cstm border border-[#D6D6D6] rounded-md px-4 py-1.5 text-sm bg-white focus:outline-none focus:border-[#358ECD] disabled:bg-gray-100 disabled:text-gray-400"
              >
                {selectedIds.length === 0 && <option value="">—</option>}
                {dashboards
                  .filter((d) => selectedIds.includes(d.id))
                  .map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
          </section>

          <div className="border-t border-gray-100" />

          {/* ── Gallery Link ── */}
          <section>
            <h3 className="common-size18 text-sm font-bold text-[#4D4D4D] mb-4">Gallery Link</h3>
            <p className="common-size15 text-xs text-[#4D4D4D] mb-4">
              <strong>Note:</strong> All media associated with a public site or a public album will be made public.
            </p>
            <p className="common-size15 text-xs font-semibold text-[#4D4D4D] mb-1">Your project&apos;s public portal gallery link:</p>
            <a href={galleryUrl} target="_blank" rel="noreferrer" className="text-[#0866AB] common-size15 font-semibold hover:underline text-sm break-all">
              {galleryUrl}
            </a>

            <p className="common-size15 text-xs font-semibold text-[#4D4D4D] mb-4 mt-4">Please select albums that you want to display in public portal:</p>
            <div className="space-y-4">
              {albumsError && (
                <p className="text-xs text-red-600">{albumsError}</p>
              )}
              {!albumsError && albums.length === 0 && (
                <p className="text-xs text-gray-500 italic">No albums in this project yet.</p>
              )}
              {albums.map((a) => (
                <label key={a.id} className="common-size18 flex items-center gap-2 font-semibold text-sm text-[#4D4D4D]">
                  <input
                    type="checkbox"
                    checked={albumIds.includes(a.id)}
                    onChange={() => toggleAlbum(a.id)}
                    className="rounded accent-clr"
                  />
                  {a.name}
                </label>
              ))}
            </div>
          </section>
          </fieldset>
        </div>

        <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-end gap-4 bg-white rounded-b-lg">
          {saveError && (
            <span className="mr-auto text-xs text-red-600">{saveError}</span>
          )}
          <button
            type="button"
            onClick={onCancel}
            className="common-size16 font-semibold px-5 py-2 text-sm text-[#358ECD] hover:underline"
          >
            {readOnly ? 'Close' : 'Cancel'}
          </button>
          {!readOnly && (
            <button
              type="button"
              onClick={submit}
              className="common-size18 font-bold px-5 py-2 bg-[#358ECD] hover:bg-[#2A78B0] text-white text-sm rounded-md"
            >
              Save
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
